import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Send } from "lucide-react";
import { Reveal, SectionTag } from "../components/Reveal";
import SEO from "../components/SEO";
import { SITE, whatsappLink } from "../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Branding", "3D & AR", "VPV, Virtual Plant Visit", "Exhibitions", "Digital / Website", "Something else"];

const initialForm = { name: "", company: "", email: "", phone: "", interest: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, company: form.company || null, phone: form.phone || null, interest: form.interest || null });
      toast.success("Enquiry sent. Nitin will get back to you shortly.");
      setForm(initialForm);
    } catch (err) {
      // Static-hosting fallback: no backend reachable, hand the enquiry to the visitor's email app.
      const subject = `Website Enquiry, ${form.name}${form.company ? ` (${form.company})` : ""}`;
      const body = `Name: ${form.name}\nCompany: ${form.company || "-"}\nEmail: ${form.email}\nPhone: ${form.phone || "-"}\nInterested in: ${form.interest || "-"}\n\nMessage:\n${form.message}`;
      window.location.href = `mailto:${SITE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      toast.info("Opening your email app to send the enquiry to Nitin, or use the WhatsApp button.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full border border-neutral-300 bg-white px-4 py-3.5 text-sm text-neutral-900 focus:outline-none focus:border-brand-red transition-colors placeholder:text-neutral-400";

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact, WhatsApp Nitin or Send an Enquiry | The Next Design"
        description="Start a conversation with Nitin Gupta at The Next Design, branding, 3D & AR, Virtual Plant Visits, exhibitions and digital for manufacturers across India."
      />
      <section className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <SectionTag>Contact</SectionTag>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light uppercase tracking-tight leading-none" data-testid="contact-heading">
              Let's talk about<br /><span className="text-brand-red">your next level.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <h2 className="font-display text-3xl font-medium tracking-tight" data-testid="contact-direct-heading">Direct lines</h2>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              The fastest way to start is a direct conversation with Nitin. No account managers, no intake forms between you and the person who leads the strategy.
            </p>
            <div className="mt-10 space-y-4">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="bg-brand-red hover:bg-brand-red-dark text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors flex items-center gap-3 w-full justify-center"
              >
                <MessageCircle size={18} /> WhatsApp Nitin
              </a>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-button"
                className="border border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors flex items-center gap-3 w-full justify-center"
              >
                <Mail size={18} /> {SITE.email}
              </a>
            </div>
            <p className="mt-8 text-sm text-neutral-500 leading-relaxed">
              Serving manufacturers across India. Typical response time: within one business day.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="border border-neutral-200 bg-neutral-50 p-8 md:p-12" data-testid="enquiry-form">
              <h2 className="font-display text-3xl font-medium tracking-tight">Send an enquiry</h2>
              <p className="mt-2 text-sm text-neutral-500">A few lines are enough, we'll take it from there.</p>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <input required value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} data-testid="enquiry-name-input" />
                <input value={form.company} onChange={set("company")} placeholder="Company" className={inputCls} data-testid="enquiry-company-input" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} data-testid="enquiry-email-input" />
                <input value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} data-testid="enquiry-phone-input" />
                <select value={form.interest} onChange={set("interest")} className={`${inputCls} md:col-span-2`} data-testid="enquiry-interest-select">
                  <option value="">What are you interested in?</option>
                  {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
                <textarea required value={form.message} onChange={set("message")} placeholder="Tell us about the business problem you're trying to solve *" rows={5} className={`${inputCls} md:col-span-2 resize-none`} data-testid="enquiry-message-input" />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-submit-button"
                className="mt-8 bg-neutral-950 hover:bg-brand-red disabled:opacity-60 text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors flex items-center gap-3 w-full md:w-auto justify-center"
              >
                {sending ? "Sending…" : "Send Enquiry"} <Send size={16} />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
